import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periods, setPeriods] = useState([]);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('astraos_token');
      
      if (savedToken) {
        try {
          // Verificar que api.defaults existe antes de acceder
          if (api.defaults && api.defaults.headers && api.defaults.headers.common) {
            api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
          }
          setToken(savedToken);
          
          // api.get returns response.data directly, so response IS the user data
          const userData = await api.get('/auth/me');
          
          // Ensure workspaceId is set
          if (userData.workspace && !userData.workspaceId) {
            userData.workspaceId = userData.workspace.id;
          }
          
          setUser(userData);
          setWorkspace(userData.workspace || null);
          
          // Load periods
          try {
            const periodsData = await api.get('/periods');
            setPeriods(periodsData || []);
          } catch (error) {
            console.error('Error loading periods:', error);
          }
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('astraos_token');
          if (api.defaults && api.defaults.headers && api.defaults.headers.common) {
            delete api.defaults.headers.common['Authorization'];
          }
          setToken(null);
          setUser(null);
        }
      }
      
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      console.log('📝 Attempting login:', email);
      
      const response = await api.post('/auth/login', { email, password });
      
      console.log('📦 Login response (data):', response);
      
      // api.post already returns response.data directly, so response IS the data
      const data = response;
      
      if (!data) {
        console.error('❌ No data in response');
        return {
          success: false,
          error: 'Respuesta vacía del servidor'
        };
      }
      
      const { token: newToken, user: userData } = data;
      
      if (!newToken || !userData) {
        console.error('❌ Missing token or user in response:', data);
        return {
          success: false,
          error: 'Respuesta inválida del servidor'
        };
      }
      
      console.log('✅ Token received:', newToken.substring(0, 20) + '...');
      console.log('✅ User data:', userData);
      
      localStorage.setItem('astraos_token', newToken);
      
      // Ensure api.defaults.headers.common exists
      if (!api.defaults.headers) {
        api.defaults.headers = {};
      }
      if (!api.defaults.headers.common) {
        api.defaults.headers.common = {};
      }
      
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      // Ensure workspaceId is set
      if (userData.workspace && !userData.workspaceId) {
        userData.workspaceId = userData.workspace.id;
      }
      
      setToken(newToken);
      setUser(userData);
      setWorkspace(userData.workspace || null);
      
      // Load periods after login
      try {
        const periodsData = await api.get('/periods');
        setPeriods(periodsData || []);
      } catch (error) {
        console.error('Error loading periods:', error);
      }
      
      console.log('✅ Login successful');
      
      return { success: true };
    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error message:', error.message);
      
      return {
        success: false,
        error: error.response?.data?.error || error.message || error.error || 'Error al iniciar sesión'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('astraos_token');
    if (api.defaults && api.defaults.headers && api.defaults.headers.common) {
      delete api.defaults.headers.common['Authorization'];
    }
    setToken(null);
    setUser(null);
    setWorkspace(null);
    setSelectedPeriod(null);
    setPeriods([]);
  };

  const value = {
    user,
    setUser,
    token,
    loading,
    login,
    logout,
    workspace,
    setWorkspace,
    selectedPeriod,
    setSelectedPeriod,
    periods,
    setPeriods
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
